import { defineComponent as _defineComponent } from 'vue';
import { renderSlot as _renderSlot, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, unref as _unref, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle } from "vue";
const _hoisted_1 = ["aria-expanded"];
const _hoisted_2 = {
    key: 0,
    class: "rec-selector__text"
};
const _hoisted_3 = { key: 0 };
const _hoisted_4 = {
    key: 1,
    class: "rec-selector__text"
};
import { onClickOutside, useElementBounding } from "@vueuse/core";
import { computed, inject, ref, useTemplateRef, watch, watchEffect } from "vue";
import { useDropdownAccessibility } from "@/common/composables/dropdown-accessibility";
import { vTooltip } from "@/common/directives/tip-directive";
// Components
import SvgIcon from "@/common/components/SvgIcon.vue";
import SelectorMenu from "./SelectorMenu.vue";
import { MenuItemType } from "@/common/composables/dropdown-accessibility";
export default /*@__PURE__*/ _defineComponent({
    __name: 'SelectorBase',
    props: {
        modelValue: {},
        config: {},
        label: {},
        singleUse: { type: Boolean },
        disabled: { type: Boolean },
        highlightEmpty: { type: Boolean }
    },
    emits: ["update:modelValue"],
    setup(__props, { emit: __emit }) {
        const i18n = inject("i18n");
        const props = __props;
        const model = ref(props.modelValue); //: useVModel(props, "modelValue");
        const emit = __emit;
        if (props.config.defaultValue) {
            if (model.value == undefined ||
                (model.value?.ids == undefined && model.value?.id == undefined)) {
                const optionSelected = props.config.options?.find((option) => {
                    if (Array.isArray(option)) {
                        return option.some((o) => o.id == props.config.defaultValue);
                    }
                    else if ("name" in option) {
                        return option.id == props.config.defaultValue;
                    }
                    return false;
                });
                if (optionSelected) {
                    const value = { id: props.config.defaultValue };
                    if (props.config.multiple && value.id) {
                        if (model.value == undefined)
                            model.value = { ids: [] };
                        if (model.value.ids == undefined)
                            model.value.ids = [];
                        model.value.ids?.push(value.id);
                    }
                    else {
                        model.value = value;
                    }
                    emit("update:modelValue", model.value);
                }
            }
        }
        const showMenu = ref(false);
        const buttonRef = useTemplateRef("buttonRef");
        const selectorRootRef = useTemplateRef("selectorRootRef");
        const menuRef = useTemplateRef("menuRef");
        const disabled = computed(() => props.disabled || props.config.readonly || (props.config.options != undefined && props.config.options.length <= 0));
        const buttonBounds = useElementBounding(buttonRef);
        const menuBounds = useElementBounding(menuRef);
        const menuStyle = computed(() => {
            const whitespace = 3;
            const viewportHeight = window.innerHeight;
            // These values will be updated and used to position the menu
            let top = buttonBounds.top.value + buttonBounds.height.value + whitespace;
            let maxHeight;
            // Check if menu overflows bottom
            if (top + menuBounds.height.value >= viewportHeight - whitespace) {
                const spaceAbove = buttonBounds.top.value - whitespace;
                const spaceBelow = viewportHeight - (buttonBounds.top.value + buttonBounds.height.value) - whitespace;
                if (spaceAbove > spaceBelow) {
                    // Open menu above if there's more space
                    top = buttonBounds.top.value - menuBounds.height.value - whitespace;
                    maxHeight = `${spaceAbove - whitespace}px`;
                }
                else {
                    // Keep menu below, but limit its height
                    maxHeight = `${spaceBelow - whitespace}px`;
                }
            }
            const style = {
                top: `${top}px`,
                left: `${buttonBounds.left.value}px`,
                width: `${buttonBounds.width.value}px`,
                ...(maxHeight && { maxHeight: `min(${maxHeight}, 25rem)` })
            };
            return style;
        });
        const isComplete = (value, options) => {
            if (options == undefined)
                options = props.config.options ?? [];
            const choice = value;
            if (choice == undefined || choice.id == undefined)
                return false;
            const option = getOption(choice.id, options);
            if (option == undefined)
                return false;
            if (option.subOptions) {
                return choice.subChoice != undefined && isComplete(choice.subChoice, option.subOptions);
            }
            return true;
        };
        // const lastModel = ref<SelectorChoice | SelectorChoice[]>(model.value);
        watch(model, (newValue) => {
            if (newValue && (props.config.multiple || isComplete(newValue))) {
                emit("update:modelValue", newValue);
                if (!props.config.multiple) {
                    showMenu.value = false;
                }
            }
        });
        watch(showMenu, () => {
            if (!props.config.readonly) {
                model.value = props.modelValue;
            }
        });
        const toggleMenu = () => {
            buttonBounds.update();
            if (!props.config.readonly) {
                showMenu.value = !showMenu.value;
            }
        };
        onClickOutside(menuRef, () => (showMenu.value = false), { ignore: [menuRef, buttonRef] });
        const selectorWrapClasses = computed(() => {
            return {
                "rec-selector-wrap": true,
                "rec-selector-wrap--open": showMenu.value
            };
        });
        const buttonClasses = computed(() => {
            return {
                "form-control": true,
                "rec-selector": true,
                "rec-selector--open": showMenu.value,
                "rec-selector--disabled": disabled.value,
                "rec-selector--placeholder": !modelChoice.value && !disabled.value,
                "rc-highlight": props.highlightEmpty && !modelChoice.value && !disabled.value
            };
        });
        const getSelectableOptions = (options) => {
            return options
                .flatMap((option) => {
                if ("options" in option) {
                    return [...option.options];
                }
                return [option];
            })
                .filter((option) => {
                return !("header" in option);
            })
                .map((option) => {
                return option;
            });
        };
        const getOption = (id, options) => {
            return getSelectableOptions(options).find((opt) => {
                if (Array.isArray(opt)) {
                    return opt.some((o) => o.id == id);
                }
                else {
                    return opt.id == id;
                }
            });
        };
        const breadcrumb = computed(() => {
            return (!props.config.multiple &&
                isComplete(props.modelValue) &&
                props.modelValue?.subChoice != undefined);
        });
        const breadcrumbs = computed(() => {
            return props.config.options && getCrumbs(props.modelValue, props.config.options);
        });
        const getCrumbs = (choice, options) => {
            const crumbs = [];
            if (choice?.id) {
                const option = getOption(choice.id, options);
                if (option) {
                    crumbs.push(option);
                    if (choice.subChoice && option.subOptions) {
                        crumbs.push(...getCrumbs(choice.subChoice, option.subOptions));
                    }
                }
            }
            return crumbs;
        };
        const modelChoice = computed(() => {
            return getName(props.modelValue, props.config?.options);
        });
        const getName = (choice, options) => {
            if (choice == undefined || options == undefined) {
                return "";
            }
            if ("ids" in choice && choice.ids) {
                if (choice.ids.length > 1) {
                    if (props.config.label) {
                        return choice.ids.length + " " + props.config.label;
                    }
                    else {
                        return i18n.t("{0} selected", [choice.ids.length.toString()]);
                    }
                }
                choice = { id: choice.ids[0] };
            }
            choice = choice;
            if (!choice || !choice?.id || !options) {
                return "";
            }
            if (choice.id) {
                const option = getOption(choice.id, options);
                if (!option) {
                    return "";
                }
                return option?.name;
            }
            return "";
        };
        const selectLabel = computed(() => {
            return props.label
                ? props.label
                : (props.config.multiple &&
                    props.modelValue &&
                    "ids" in props.modelValue &&
                    props.modelValue.ids &&
                    props.modelValue.ids?.length > 0) ||
                    (!props.config.multiple && isComplete(props.modelValue))
                    ? modelChoice.value
                    : props.config.condensed
                        ? props.config.label
                        : i18n.t("Select") + "...";
        });
        watchEffect((onCleanup) => {
            const disposeEventListener = useDropdownAccessibility({
                isOpen: showMenu,
                eventTargetEl: selectorRootRef,
                toggleSubMenu: toggleMenu,
                menuToggleBtn: buttonRef,
                menuItemBtnType: MenuItemType.DROPDOWN,
                submenu: menuRef
            });
            onCleanup(() => disposeEventListener());
        });
        const selectButtonTip = computed(() => {
            let tip = selectLabel.value;
            if (breadcrumb.value && breadcrumbs.value) {
                tip = breadcrumbs.value
                    .map((crumb, idx, arr) => {
                    let c = crumb.condensed ? "<strong>" + crumb.name + "</strong>" : crumb.name;
                    if (idx < arr.length - 1)
                        c += crumb.condensed ? " : " : " > ";
                    return c;
                })
                    .join("");
            }
            return tip && tip.length > 30 ? tip : null;
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                ref_key: "selectorRootRef",
                ref: selectorRootRef,
                class: _normalizeClass(selectorWrapClasses.value)
            }, [
                _withDirectives((_openBlock(), _createElementBlock("button", {
                    ref_key: "buttonRef",
                    ref: buttonRef,
                    "aria-haspopup": "true",
                    "aria-expanded": showMenu.value,
                    type: "button",
                    class: _normalizeClass(_ctx.$slots.default ? 'rec-selector-custom' : buttonClasses.value),
                    onClick: toggleMenu
                }, [
                    _renderSlot(_ctx.$slots, "default", {}, () => [
                        (breadcrumb.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
                                _createElementVNode("span", null, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(breadcrumbs.value, (crumb, idx) => {
                                        return (_openBlock(), _createElementBlock(_Fragment, { key: idx }, [
                                            (crumb.condensed)
                                                ? (_openBlock(), _createElementBlock("strong", _hoisted_3, _toDisplayString(crumb.name), 1))
                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                    _createTextVNode(_toDisplayString(crumb.name), 1)
                                                ], 64)),
                                            (idx < breadcrumbs.value.length - 1)
                                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                                    _createTextVNode(" " + _toDisplayString(crumb.condensed ? ":" : ">") + " ", 1)
                                                ], 64))
                                                : _createCommentVNode("", true)
                                        ], 64));
                                    }), 128))
                                ])
                            ]))
                            : (_openBlock(), _createElementBlock("span", _hoisted_4, [
                                _createElementVNode("span", null, _toDisplayString(selectLabel.value), 1)
                            ])),
                        (!_ctx.config.readonly)
                            ? (_openBlock(), _createBlock(SvgIcon, {
                                key: 2,
                                class: "rec-selector__arrow",
                                icon: "icon-arrow-down"
                            }))
                            : _createCommentVNode("", true)
                    ])
                ], 10, _hoisted_1)), [
                    [_unref(vTooltip), selectButtonTip.value]
                ]),
                (showMenu.value && !_ctx.config.readonly && _ctx.config.options)
                    ? (_openBlock(), _createBlock(SelectorMenu, {
                        key: 0,
                        ref_key: "menuRef",
                        ref: menuRef,
                        modelValue: model.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((model.value) = $event)),
                        multiple: _ctx.config.multiple ?? false,
                        style: _normalizeStyle(menuStyle.value),
                        options: _ctx.config.options,
                        "menu-toggle-btn": _unref(buttonRef)
                    }, null, 8, ["modelValue", "multiple", "style", "options", "menu-toggle-btn"]))
                    : _createCommentVNode("", true)
            ], 2));
        };
    }
});
